import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, FileText, Gauge, Layers, Settings2, UserCog, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePlatform } from '../context/PlatformContext';
import { Alert, Button } from '../components/ui';
import AdminShell from '../components/admin/AdminShell';
import useConnectionState from '../components/admin/useConnectionState';
import SourcesConfig from '../components/admin/SourcesConfig';
import DocumentLibrary from '../components/admin/DocumentLibrary';
import LearningPaths from '../components/admin/LearningPaths';
import GroupManagement from '../components/admin/GroupManagement';
import UserManagement from '../components/admin/UserManagement';
import AccessTokens from '../components/admin/AccessTokens';
import LlmPolicySettings from '../components/admin/LlmPolicySettings';
import LearningNeeds from '../components/admin/LearningNeeds';
import { adminSectionIds, canOpenAdmin } from '../utils/adminAccess.js';

/** Sections that moved to `/settings` in the Etch split (spec §8.1). */
const MOVED_TO_SETTINGS = ['theme', 'ai', 'persona', 'tokens', 'tenant', 'language', 'notifications', 'accessibility'];

const ADMIN_SECTIONS = ['sources', 'documents', 'paths', 'groups', 'users', 'tenant-defaults', 'needs'];
const DEFAULT_SECTION = 'sources';

function sectionFromPath(pathname) {
  const segment = pathname.split('/').filter(Boolean).at(-1);
  return ADMIN_SECTIONS.includes(segment) ? segment : DEFAULT_SECTION;
}

/**
 * `/admin` — the administrative half of the old combined settings surface.
 * Personal preferences now live in `SettingsPage`; this route only keeps what
 * requires an admin rank (spec §10.6) — the operator, or a tenant admin who
 * sees every section except the instance-wide ones (utils/adminAccess.js).
 */
export default function AdminPage() {
  const { t } = useTranslation();
  const { token, user, loading: authLoading } = useAuth();
  const { sources } = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const online = useConnectionState();
  const isAdmin = canOpenAdmin(user);
  const [activeSection, setActiveSection] = useState(() => sectionFromPath(location.pathname));

  // Deep links into the pre-split preference tabs must keep working, so a
  // legacy `/admin/<pref>` URL is forwarded rather than silently reset.
  const legacySegment = location.pathname.split('/').filter(Boolean).at(-1);
  const movedSection = MOVED_TO_SETTINGS.includes(legacySegment) ? legacySegment : null;

  useEffect(() => {
    if (movedSection) navigate(`/settings/${movedSection}`, { replace: true });
  }, [movedSection, navigate]);

  useEffect(() => {
    const fromPath = sectionFromPath(location.pathname);
    if (fromPath !== activeSection) setActiveSection(fromPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function selectSection(id) {
    setActiveSection(id);
    navigate(`/admin/${id}`, { replace: true });
  }

  if (movedSection) return null;

  // -- Non-happy paths (spec §11) ------------------------------------------
  if (authLoading) {
    return (
      <div className="etch-view etch-center" role="status" aria-live="polite">
        {t('states.loading')}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="etch-view etch-stack" data-gap="4">
        <Alert tone="warning" title={t('states.sessionExpired')}>
          {t('auth.sessionExpired')}
        </Alert>
        <Button onClick={() => navigate('/login')}>{t('auth.login')}</Button>
      </div>
    );
  }

  // Client-side gating is a UX concession only — `requireAuth` + `requireAdmin`
  // on every `/api/admin/*` route stays the actual authorisation boundary.
  if (!isAdmin) {
    return (
      <div className="etch-view etch-stack" data-gap="4">
        <h1 className="etch-page-header__title">{t('nav.admin')}</h1>
        <Alert tone="warning" title={t('states.permissionDenied')}>
          {t('admin.adminOnlyHint')}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/settings')}>
          {t('nav.settings')}
        </Button>
      </div>
    );
  }

  const groupLabels = {
    knowledge: t('admin.groupKnowledge'),
    people: t('admin.groupPeople'),
    tenant: t('admin.groupTenant'),
  };
  const allItems = [
    { id: 'sources', label: t('admin.sources'), icon: Settings2, group: 'knowledge' },
    { id: 'documents', label: t('admin.documents'), icon: FileText, group: 'knowledge' },
    { id: 'paths', label: t('admin.paths'), icon: BookOpen, group: 'knowledge' },
    { id: 'groups', label: t('admin.groups'), icon: Users, group: 'people' },
    { id: 'users', label: t('admin.users'), icon: UserCog, group: 'people' },
    { id: 'needs', label: t('admin.learningNeeds'), icon: Gauge, group: 'people' },
    { id: 'tenant-defaults', label: t('admin.tenantDefaults'), icon: Layers, group: 'tenant' },
  ];
  const visible = adminSectionIds(user, { documentsEnabled: Boolean(sources.documents?.enabled) });
  // The group label rides on the first visible item of its group, so it is
  // assigned after filtering — a tenant admin has no `sources` tab to carry it.
  const items = allItems
    .filter((item) => visible.includes(item.id))
    .map((item, i, list) => (i === 0 || list[i - 1].group !== item.group
      ? { ...item, groupLabel: groupLabels[item.group] }
      : item));

  // A section can disappear (documents is gated on the source being enabled,
  // sources on the operator rank); never leave the tablist pointing at nothing.
  const resolvedSection = items.some((item) => item.id === activeSection) ? activeSection : items[0]?.id;

  return (
    <AdminShell
      title={t('nav.admin')}
      idPrefix="admin"
      items={items}
      activeId={resolvedSection}
      onSelect={selectSection}
    >
      {!online && (
        <Alert tone="warning" title={t('states.offline')}>
          {t('admin.offlineHint')}
        </Alert>
      )}

      {resolvedSection === 'sources' && <SourcesConfig token={token} />}
      {resolvedSection === 'documents' && <DocumentLibrary token={token} />}
      {resolvedSection === 'paths' && <LearningPaths token={token} />}
      {resolvedSection === 'groups' && <GroupManagement token={token} />}
      {resolvedSection === 'users' && <UserManagement token={token} />}
      {resolvedSection === 'needs' && <LearningNeeds token={token} />}
      {resolvedSection === 'tenant-defaults' && (
        <div className="etch-stack" data-gap="6">
          <LlmPolicySettings />
          <AccessTokens scope="tenant" />
        </div>
      )}
    </AdminShell>
  );
}
